import { useEffect, useState } from "react";
import { TICKS_PER_SECOND, UNITS, unitStats, type UnitKind } from "@empire/core-sim";
import { UnitMark } from "../components/UnitMark.js";
import { LootLine } from "../components/LootLine.js";
import { useAttackStore } from "../../store/attack.js";
import {
  affordable,
  secondsOf,
  summarise,
  unitPrice,
  useHeldArmy,
  usePlanStore,
  type WaveDraft,
} from "../../store/plan.js";
import { useUiStore } from "../../store/ui.js";
import { useVillageStore } from "../../store/village.js";
import { IntelPanel } from "../components/IntelPanel.js";
import { useReplayStore } from "../../store/replay.js";

/**
 * Composing and sending an attack (S3-1, S3-4).
 *
 * Since D-109 the planner spends the army standing in the village rather than a
 * budget: every chip is capped by what is actually in the barracks, and what you
 * send and lose is gone until you train more. The number at the bottom is
 * therefore how many units against the cap, not how many resources against an
 * allowance — the allowance no longer exists.
 */
export function AttackPlanner(): JSX.Element {
  const waves = usePlanStore((s) => s.waves);
  const target = useAttackStore((s) => s.confirming);
  const outcome = useAttackStore((s) => s.outcome);
  const have = useHeldArmy();
  const loaded = useVillageStore((s) => s.economy !== null);
  const plan = summarise(waves, have);

  // The army changes under the plan: a battle kills units, training adds them.
  // Never plan more than is at home.
  useEffect(() => {
    if (loaded) usePlanStore.getState().fitTo(have);
  }, [have, loaded]);

  if (outcome) return <Outcome />;

  return (
    <div className="flex h-full flex-col">
      <TargetBar />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <IntelPanel />
        <UnitChips />
        <Waves />
      </div>
      <LaunchBar canLaunch={plan.problem === null && target !== null} />
    </div>
  );
}

/** Who this army is for, or a prompt to go and choose. */
function TargetBar(): JSX.Element {
  const target = useAttackStore((s) => s.confirming);
  const cancel = useAttackStore((s) => s.cancel);
  const setTab = useUiStore((s) => s.setTab);

  if (!target) {
    return (
      <div className="shrink-0 border-b border-panel-edge bg-bg-raised px-4 py-3">
        <p className="text-sm text-ink">No target chosen.</p>
        <button
          type="button"
          onClick={() => setTab("map")}
          className="mt-1.5 h-11 rounded-lg bg-gold px-4 text-xs font-bold text-bg"
        >
          Pick someone to attack
        </button>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-panel-edge bg-bg-raised px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-ink-dim opacity-70">attacking</p>
        <p className="truncate text-sm font-semibold text-ink">
          {target.villageName}{" "}
          <span className="font-normal text-ink-dim">· {target.playerName}</span>
        </p>
      </div>
      <button
        type="button"
        onClick={cancel}
        className="h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim hover:text-ink"
      >
        Change
      </button>
    </div>
  );
}

/**
 * One chip per unit type.
 *
 * A unit you have none of is greyed with its name and price still on it, not
 * hidden — the same reasoning as the tower tray: a player should be able to see
 * that the Ram exists, and that it would cost 640 to train one, before they
 * decide the Workshop is worth building.
 */
export function UnitChips({ only }: { only?: readonly UnitKind[] } = {}): JSX.Element {
  const waves = usePlanStore((s) => s.waves);
  const add = usePlanStore((s) => s.add);
  const active = usePlanStore((s) => s.active);
  const have = useHeldArmy();

  return (
    <div className="border-b border-panel-edge bg-bg-raised px-2 py-2">
      <div className="flex items-center justify-between px-1 pb-1.5">
        <p className="text-[10px] uppercase tracking-wide text-ink-dim opacity-70">
          add to wave {active + 1}
        </p>
        <button
          type="button"
          data-testid="add-all"
          disabled={UNITS.every((u) => affordable(waves, u.id as UnitKind, have) === 0)}
          onClick={() => {
            // Everything still at home goes into this wave: ten taps for ten
            // Militia was the planner's most repeated action.
            for (const u of UNITS) {
              const kind = u.id as UnitKind;
              const room = affordable(waves, kind, have);
              if (room > 0) add(kind, room);
            }
          }}
          className="h-8 rounded-md bg-panel px-2.5 text-[11px] font-semibold text-ink-dim enabled:hover:text-ink disabled:opacity-35"
        >
          + all remaining
        </button>
      </div>
      <div className="flex gap-1 overflow-x-auto no-scrollbar">
        {UNITS.filter(
          (unit) => unit.combat !== false && (!only || only.includes(unit.id as UnitKind))
        ).map((unit) => {
          const kind = unit.id as UnitKind;
          const stats = unitStats(kind);
          const owned = have[kind] ?? 0;
          const room = affordable(waves, kind, have);
          return (
            <button
              key={kind}
              type="button"
              data-testid={`chip-${kind}`}
              disabled={room === 0}
              title={
                owned === 0
                  ? `${stats.role} · none trained · ${unitPrice(kind)} resources each`
                  : `${stats.role} · ${owned} in the village`
              }
              onClick={() => add(kind)}
              className="min-w-[84px] shrink-0 rounded-lg bg-panel px-2.5 py-1.5 text-left text-ink-dim enabled:hover:text-ink disabled:opacity-35"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold">
                <UnitMark kind={kind} />
                {stats.name}
              </span>
              <span className="block text-[10px] tabular-nums opacity-80">
                {owned === 0 ? "none trained" : `${room} of ${owned} left`}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Waves(): JSX.Element {
  const waves = usePlanStore((s) => s.waves);
  const addWave = usePlanStore((s) => s.addWave);

  return (
    <div className="flex flex-col gap-2 p-3">
      {waves.map((wave, index) => (
        <WaveCard key={index} wave={wave} index={index} />
      ))}
      <button
        type="button"
        onClick={addWave}
        disabled={waves.length >= 8}
        className="h-11 rounded-lg border border-dashed border-panel-edge text-xs font-semibold text-ink-dim enabled:hover:text-ink disabled:opacity-35"
      >
        + Add a wave
      </button>
      <p className="px-1 text-[11px] text-ink-dim opacity-80">
        Waves enter one after another. A later wave walks into towers the first one already made
        fire — or into a maze the first one cleared a barricade out of.
      </p>
    </div>
  );
}

function WaveCard({ wave, index }: { wave: WaveDraft; index: number }): JSX.Element {
  const active = usePlanStore((s) => s.active);
  const setActive = usePlanStore((s) => s.setActive);
  const setCount = usePlanStore((s) => s.setCount);
  const removeWave = usePlanStore((s) => s.removeWave);
  const nudge = usePlanStore((s) => s.nudgeOffset);
  const waves = usePlanStore((s) => s.waves);
  const have = useHeldArmy();

  const kinds = UNITS.map((u) => u.id as UnitKind).filter((k) => (wave.counts[k] ?? 0) > 0);
  const size = kinds.reduce((n, k) => n + (wave.counts[k] ?? 0), 0);

  return (
    <div
      data-testid={`wave-${index}`}
      onClick={() => setActive(index)}
      className={`rounded-xl border px-3 py-2.5 ${
        index === active ? "border-gold bg-panel" : "border-panel-edge bg-panel/60"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-ink">Wave {index + 1}</span>
        <span className="text-[11px] tabular-nums text-ink-dim">{size} units</span>

        <div className="ml-auto flex items-center gap-1">
          {index === 0 ? (
            // Wave 1 defines the clock the others are measured against, so there
            // is nothing to move it relative to.
            <span className="px-2 text-[11px] text-ink-dim">enters first</span>
          ) : (
            <>
              <button
                type="button"
                aria-label="Enter earlier"
                onClick={(e) => {
                  e.stopPropagation();
                  nudge(index, -1);
                }}
                disabled={wave.offsetTicks <= 0}
                className="h-11 w-11 rounded-lg bg-bg-raised text-base font-bold text-ink-dim enabled:hover:text-ink disabled:opacity-30"
              >
                −
              </button>
              <span
                className="w-14 text-center text-[11px] tabular-nums text-ink"
                data-testid={`offset-${index}`}
              >
                +{secondsOf(wave.offsetTicks).toFixed(1)}s
              </span>
              <button
                type="button"
                aria-label="Enter later"
                onClick={(e) => {
                  e.stopPropagation();
                  nudge(index, 1);
                }}
                className="h-11 w-11 rounded-lg bg-bg-raised text-base font-bold text-ink-dim hover:text-ink"
              >
                +
              </button>
            </>
          )}
          <button
            type="button"
            aria-label={`Remove wave ${index + 1}`}
            onClick={(e) => {
              e.stopPropagation();
              removeWave(index);
            }}
            className="h-11 w-11 rounded-lg bg-bg-raised text-xs font-semibold text-danger hover:bg-panel-edge"
          >
            ✕
          </button>
        </div>
      </div>

      {kinds.length === 0 ? (
        <p className="mt-1.5 text-[11px] text-ink-dim opacity-70">
          Empty. Tap a unit above to add it here.
        </p>
      ) : (
        <div className="mt-2 flex flex-col gap-1">
          {kinds.map((kind) => (
            <div key={kind} className="flex items-center gap-2">
              <UnitMark kind={kind} size={12} />
              <span className="min-w-0 flex-1 truncate text-[11px] text-ink-dim">
                {unitStats(kind).name}
              </span>
              <button
                type="button"
                aria-label={`One fewer ${unitStats(kind).name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCount(index, kind, (wave.counts[kind] ?? 0) - 1);
                }}
                className="h-11 w-11 rounded-lg bg-bg-raised text-base font-bold text-ink-dim hover:text-ink"
              >
                −
              </button>
              <span
                className="w-8 text-center text-xs tabular-nums text-ink"
                data-testid={`count-${index}-${kind}`}
              >
                {wave.counts[kind]}
              </span>
              <button
                type="button"
                aria-label={`One more ${unitStats(kind).name}`}
                // Greyed out once every one you own is in the plan: the
                // planner stops you here instead of refusing at Send.
                disabled={affordable(waves, kind, have) === 0}
                onClick={(e) => {
                  e.stopPropagation();
                  setCount(index, kind, (wave.counts[kind] ?? 0) + 1);
                }}
                className="h-11 w-11 rounded-lg bg-bg-raised text-base font-bold text-ink-dim enabled:hover:text-ink disabled:opacity-30"
              >
                +
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** What is being sent, what it will leave behind, and the button. */
function LaunchBar({ canLaunch }: { canLaunch: boolean }): JSX.Element {
  const waves = usePlanStore((s) => s.waves);
  const clear = usePlanStore((s) => s.clear);
  const send = useAttackStore((s) => s.send);
  const status = useAttackStore((s) => s.status);
  const error = useAttackStore((s) => s.error);
  const target = useAttackStore((s) => s.confirming);
  const have = useHeldArmy();
  const plan = summarise(waves, have);
  const [confirming, setConfirming] = useState(false);

  const garrison = UNITS.reduce((n, u) => n + (have[u.id as UnitKind] ?? 0), 0);

  return (
    <div
      className="shrink-0 border-t border-panel-edge bg-bg-raised"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {error ? (
        <p className="border-b border-danger/30 bg-danger/10 px-3 py-2 text-[11px] text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-3 px-4 py-3">
        <div className="min-w-0 flex-1 text-[11px] tabular-nums">
          <p data-testid="plan-cost" className="text-ink-dim">
            {plan.size} of your {garrison} {garrison === 1 ? "unit" : "units"}
            {plan.size > 0 ? ` · ${garrison - plan.size} stay home` : ""}
          </p>
          <p data-testid="plan-size" className="text-ink-dim opacity-80">
            {plan.size} units · {plan.supply} / {plan.supplyCap} supply
            {plan.size > 0
              ? ` · ${plan.plan.waves.length} ${plan.plan.waves.length === 1 ? "wave" : "waves"}`
              : ""}
          </p>
          {plan.problem && !plan.empty ? (
            <p className="mt-0.5 text-danger" data-testid="plan-problem">
              {plan.problem}
            </p>
          ) : null}
        </div>

        <button
          type="button"
          onClick={clear}
          disabled={plan.empty}
          className="h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim enabled:hover:text-ink disabled:opacity-35"
        >
          Clear
        </button>
        <button
          type="button"
          data-testid="launch-button"
          disabled={!canLaunch || status === "sending"}
          title={
            !target
              ? "Pick a target first"
              : (plan.problem ?? `Send ${plan.size} units at ${target.villageName}`)
          }
          onClick={() => setConfirming(true)}
          className="h-11 shrink-0 rounded-lg bg-gold px-4 text-xs font-bold text-bg disabled:opacity-40"
        >
          {status === "sending" ? "Sending…" : "Attack"}
        </button>
      </div>

      {confirming && target ? (
        <ConfirmSheet
          size={plan.size}
          waves={plan.plan.waves.length}
          targetName={target.villageName}
          onCancel={() => setConfirming(false)}
          onSend={() => {
            setConfirming(false);
            void send();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * One last look before the army leaves (S3-4's confirm sheet).
 *
 * Since D-109 an attack spends real units, and the walkthrough of 19 Sep had a
 * new player's whole starting army die on the first tap of Attack with no
 * warning. The sheet says the one thing that matters: what dies is gone.
 */
function ConfirmSheet(props: {
  size: number;
  waves: number;
  targetName: string;
  onCancel: () => void;
  onSend: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-40 flex items-end bg-black/60"
      onClick={props.onCancel}
      data-testid="confirm-sheet"
    >
      <div
        className="w-full rounded-t-2xl border-t border-panel-edge bg-bg-raised px-4 pt-4"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-base font-semibold text-ink">
          Send {props.size} {props.size === 1 ? "unit" : "units"} at {props.targetName}?
        </p>
        <p className="mt-1 text-xs text-ink-dim">
          {props.waves} {props.waves === 1 ? "wave" : "waves"}. The battle is decided the moment you
          send it. Units that die do not come back — you will have to train more.
        </p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={props.onCancel}
            className="h-12 flex-1 rounded-lg border border-panel-edge bg-panel text-sm font-semibold text-ink-dim"
          >
            Not yet
          </button>
          <button
            type="button"
            data-testid="confirm-send"
            onClick={props.onSend}
            className="h-12 flex-1 rounded-lg bg-gold text-sm font-bold text-bg"
          >
            Send them
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What happened.
 *
 * Deliberately blunt about the fog: an attacker who was held is TOLD they only
 * saw part of the maze, because the whole point of attacking again is knowing
 * there is more to find out.
 */
function Outcome(): JSX.Element {
  const outcome = useAttackStore((s) => s.outcome)!;
  const dismiss = useAttackStore((s) => s.dismissOutcome);
  const leave = useAttackStore((s) => s.leave);
  const breached = outcome.result.outcome === "breach";
  const { sent, reached, lost, survived } = outcome.result.army;

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <p
          className={`font-display text-3xl ${breached ? "text-ok" : "text-danger"}`}
          data-testid="outcome"
        >
          {breached ? "Victory" : "Defeat"}
        </p>
        <p className="mt-1 text-sm text-ink-dim">
          {breached
            ? `You broke through: ${reached} of your ${sent} reached the core.`
            : `Your attack was stopped. None of your ${sent} reached the core.`}
        </p>

        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] tabular-nums text-ink-dim">
          <Stat label="sent" value={sent} />
          <Stat label="reached" value={reached} />
          <Stat label="lost" value={lost} />
          <Stat label="turned back" value={survived} />
          <Stat label="lasted" value={`${(outcome.result.ticks / TICKS_PER_SECOND).toFixed(1)}s`} />
        </dl>

        <div className="mt-3">
          <LootLine
            loot={outcome.result.loot}
            note={outcome.result.lootNote}
            economics={outcome.result}
            side="attacker"
            testId="outcome-loot"
          />
        </div>

        <Bill />

        <div className="mt-4 rounded-xl border border-panel-edge bg-panel px-3 py-2.5">
          <p className="text-xs font-semibold text-ink">
            {breached
              ? "You saw the whole defence"
              : `${outcome.view.unknownCount} tiles still unknown`}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-dim">
            {breached
              ? "Breaking through buys the complete picture: every road tile and every tower."
              : "You only learned the road you walked and the towers that fired at you. The rest of the maze was never sent to your phone — attack again to see more of it."}
          </p>
        </div>
      </div>

      <div
        className="shrink-0 border-t border-panel-edge bg-bg-raised px-4 py-3"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
      >
        <button
          type="button"
          data-testid="back-to-map"
          onClick={leave}
          className="mb-2 h-11 w-full rounded-lg bg-gold text-xs font-bold text-bg"
        >
          Back to the map
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="watch-battle"
            onClick={() => void useReplayStore.getState().open(outcome.battleId, "attacker")}
            className="h-11 flex-1 rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Watch again
          </button>
          <button
            type="button"
            data-testid="attack-again"
            onClick={dismiss}
            className="h-11 flex-1 rounded-lg border border-panel-edge bg-panel text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Attack again
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * What the raid actually cost, by unit.
 *
 * Since D-109 this is the part a player feels: "lost 11" is a statistic, and
 * "you are down two Rams" is a thing you have to go and rebuild. Nothing is
 * shown when nobody died, because an empty panel saying "losses: none" is
 * noise on a result screen that is already busy.
 */
function Bill(): JSX.Element | null {
  const outcome = useAttackStore((s) => s.outcome)!;
  const losses = UNITS.map((u) => u.id as UnitKind)
    .map((kind) => ({ kind, count: outcome.result.losses[kind] ?? 0 }))
    .filter((row) => row.count > 0);
  if (losses.length === 0) return null;

  return (
    <div className="mt-4" data-testid="losses">
      <p className="text-[10px] uppercase tracking-wide text-ink-dim opacity-70">never came home</p>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
        {losses.map(({ kind, count }) => (
          <span key={kind} className="flex items-center gap-1.5 text-[11px] text-ink-dim">
            <UnitMark kind={kind} size={12} />
            <span className="tabular-nums text-danger">{count}</span> {unitStats(kind).name}
          </span>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }): JSX.Element {
  return (
    <div className="flex justify-between gap-2">
      <dt>{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}
